import { useState } from 'react';
import {
    Modal,
    Stack,
    Group,
    Text,
    Button,
    Card,
    Badge,
    ScrollArea,
    Divider,
    Alert,
    Grid,
    Paper,
    ThemeIcon,
    ActionIcon,
    Collapse,
    Box
} from '@mantine/core';
import {
    IconArrowBack,
    IconPackage,
    IconChevronDown,
    IconChevronUp,
    IconScale,
    IconRuler,
    IconTruck,
    IconBrandWhatsapp,
    IconMail
} from '@tabler/icons-react';
import { Package } from './PackageList';

interface ReturnModalProps {
    opened: boolean;
    onClose: () => void;
    selectedPackages: Package[];
    isMobile?: boolean;
}

const ReturnModal = ({
    opened,
    onClose,
    selectedPackages,
    isMobile = false,
}: ReturnModalProps) => {
    const [expandedPackages, setExpandedPackages] = useState<Set<number>>(new Set());

    const togglePackageExpanded = (packageId: number) => {
        const newExpanded = new Set(expandedPackages);
        if (newExpanded.has(packageId)) {
            newExpanded.delete(packageId);
        } else {
            newExpanded.add(packageId);
        }
        setExpandedPackages(newExpanded);
    };

    // Function to generate package details text for messages
    const getPackageDetailsText = () => {
        const packageIds = selectedPackages.map(pkg => pkg.id).join(', ');
        return `Hello, I would like to return the following packages to sender: ${packageIds}`;
    };

    // Handle WhatsApp redirect
    const handleWhatsAppClick = () => {
        const packageDetails = getPackageDetailsText();
        // WhatsApp business number
        const whatsappUrl = `https://wa.me/+94777846800?text=${encodeURIComponent(packageDetails)}`;
        window.open(whatsappUrl, '_blank');
        onClose();
    };

    // Handle Email redirect
    const handleEmailClick = () => {
        const packageDetails = getPackageDetailsText();
        // Company support email
        const emailSubject = `Return Request for Package(s): ${selectedPackages.map(pkg => pkg.id).join(', ')}`;
        const emailBody = packageDetails;
        const mailtoUrl = `mailto:otlexpress@otlcolombo.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        window.location.href = mailtoUrl;
        onClose();
    };

    const formatDimensions = (dimensions: any) => {
        if (!dimensions) return 'N/A';
        return `${dimensions.length}"×${dimensions.width}"×${dimensions.height}"`;
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="sm">
                    <ThemeIcon color="orange" variant="light" size={isMobile ? 'sm' : 'md'}>
                        <IconArrowBack size={isMobile ? 14 : 18} />
                    </ThemeIcon>
                    <Text fw={600} size={isMobile ? 'sm' : 'md'}>
                        Return {selectedPackages.length} Package{selectedPackages.length > 1 ? 's' : ''} to Sender
                    </Text>
                </Group>
            }
            centered
            size={isMobile ? 'xs' : 'lg'}
            padding={isMobile ? 'sm' : 'md'}
            scrollAreaComponent={ScrollArea.Autosize}
        >
            <Stack gap={isMobile ? 'sm' : 'md'}>
                {/* Info Alert */}
                <Alert
                    icon={<IconTruck size={16} />}
                    title="Return to Sender"
                    color="orange"
                    variant="light"
                    __size={isMobile ? 'sm' : 'md'}
                >
                    <Text size={isMobile ? 'xs' : 'sm'}>
                        If you want to return your package to the sender, please contact us with your parcel ID through WhatsApp or email. We will reply to your request as soon as possible.
                    </Text>
                </Alert>

                {/* Package Overview */}
                <Card withBorder radius="md" p={isMobile ? 'sm' : 'md'}>
                    <Group justify="space-between" mb="sm">
                        <Text fw={600} size={isMobile ? 'sm' : 'md'}>
                            Package Overview
                        </Text>
                        <Badge variant="light" color="orange" size={isMobile ? 'sm' : 'md'}>
                            {selectedPackages.length} item{selectedPackages.length > 1 ? 's' : ''}
                        </Badge>
                    </Group>

                    <Stack gap={isMobile ? 'xs' : 'sm'}>
                        {selectedPackages.map((pkg) => (
                            <Paper key={pkg.id} withBorder p={isMobile ? 'xs' : 'sm'} radius="sm">
                                <Group justify="space-between" wrap="nowrap">
                                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                        <ThemeIcon size={isMobile ? 'sm' : 'md'} variant="light" color="blue">
                                            <IconPackage size={isMobile ? 12 : 16} />
                                        </ThemeIcon>
                                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                            <Text size={isMobile ? 'xs' : 'sm'} fw={500} lineClamp={1}>
                                                Package #{pkg.id}
                                            </Text>
                                            <Text size="xs" c="dimmed" lineClamp={1}>
                                                {pkg.description || 'No description'}
                                            </Text>
                                        </Stack>
                                    </Group>
                                    <ActionIcon
                                        variant="subtle"
                                        size={isMobile ? 'sm' : 'md'}
                                        onClick={() => togglePackageExpanded(pkg.id)}
                                    >
                                        {expandedPackages.has(pkg.id) ? (
                                            <IconChevronUp size={14} />
                                        ) : (
                                            <IconChevronDown size={14} />
                                        )}
                                    </ActionIcon>
                                </Group>

                                <Collapse in={expandedPackages.has(pkg.id)}>
                                    <Divider my="xs" />
                                    <Grid gutter={isMobile ? 'xs' : 'sm'}>
                                        <Grid.Col span={6}>
                                            <Group gap="xs">
                                                <IconScale size={12} color="gray" />
                                                <Text size="xs" c="dimmed">
                                                    Weight: {pkg.weight || 'N/A'}
                                                </Text>
                                            </Group>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Group gap="xs">
                                                <IconRuler size={12} color="gray" />
                                                <Text size="xs" c="dimmed">
                                                    {formatDimensions(pkg.dimensions)}
                                                </Text>
                                            </Group>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Text size="xs" c="dimmed">
                                                Value: ${pkg.customValue || '0.00'}
                                            </Text>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Badge size="xs" variant="light" color={pkg.status === 'RECEIVED' ? 'green' : 'yellow'}>
                                                {pkg.status}
                                            </Badge>
                                        </Grid.Col>
                                    </Grid>

                                    {pkg.item && pkg.item.length > 0 && (
                                        <Box mt="xs">
                                            <Text size="xs" fw={500} mb={2}>Items:</Text>
                                            {pkg.item.slice(0, 2).map((item, itemIndex) => (
                                                <Text key={itemIndex} size="xs" c="dimmed" pl="sm">
                                                    • {item.name} (Qty: {item.quantity})
                                                </Text>
                                            ))}
                                            {pkg.item.length > 2 && (
                                                <Text size="xs" c="dimmed" pl="sm">
                                                    ... and {pkg.item.length - 2} more item{pkg.item.length - 2 > 1 ? 's' : ''}
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                </Collapse>
                            </Paper>
                        ))}
                    </Stack>
                </Card>







                {/* Action Buttons */}
                <Group justify="flex-end" gap={isMobile ? 'xs' : 'sm'} mt="md">
                    <Button
                        color="green"
                        leftSection={<IconBrandWhatsapp size={isMobile ? 14 : 16} />}
                        onClick={handleWhatsAppClick}
                        size={isMobile ? 'sm' : 'md'}
                    >
                        Contact via WhatsApp
                    </Button>
                    <Button
                        color="blue"
                        leftSection={<IconMail size={isMobile ? 14 : 16} />}
                        onClick={handleEmailClick}
                        size={isMobile ? 'sm' : 'md'}
                    >
                        Contact via Email
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ReturnModal;